import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  AdditionalPaymentInfoInput,
  CreditCardType,
  StarkwareSignatureInput,
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useGetAuthorizationApprovals } from '@sorare/core/src/hooks/useGetAuthorizationApprovals';
import { useMigrateEth } from '@sorare/core/src/hooks/useMigrateEth';
import { usePlatform } from '@sorare/core/src/hooks/usePlatform';

import type {
  PrepareBidMutation,
  PrepareBidMutationVariables,
} from './__generated__/usePrepareBid.graphql';

const PREPARE_BID_MUTATION = gql`
  mutation PrepareBidMutation($input: prepareBidInput!) {
    prepareBid(input: $input) {
      authorizations {
        id
        request {
          ... on PaypalAuthorizationRequest {
            id
            redirectUrl
          }
          ... on MangopayKlarnaAuthorizationRequest {
            id
            redirectUrl
          }
        }
        ...useGetAuthorizationApprovals_authorizationRequest
      }
      errors {
        message
        code
      }
    }
  }
  ${useGetAuthorizationApprovals.fragments.authorizationRequest}
` as TypedDocumentNode<PrepareBidMutation, PrepareBidMutationVariables>;

type PrepareBidArgs = {
  supportedCurrency: SupportedCurrency;
  tokenPaymentMethod: TokenPaymentMethod;
  cardType?: CreditCardType;
  auctionId: string;
  amount: string;
  conversionCreditIds?: string[];
  savePaymentMethod?: boolean;
  walletChallengeSignature?: StarkwareSignatureInput | null;
  attemptReference: string | null;
  signAuthorizations: boolean;
  returnUrl?: string;
  additionalPaymentInfo?: AdditionalPaymentInfoInput;
};

export const usePrepareBid = () => {
  const getAuthorizationApprovals = useGetAuthorizationApprovals();
  const {
    flags: { nonStackableMarketCredits = false },
  } = useFeatureFlags();
  const platform = usePlatform();
  const [prepareBidMutation, { loading }] = useMutation(PREPARE_BID_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const { exchangeRate } = useConfigContext();
  const migrateEth = useMigrateEth();

  const prepareBid = async ({
    supportedCurrency,
    tokenPaymentMethod,
    auctionId,
    amount,
    conversionCreditIds,
    cardType,
    savePaymentMethod,
    walletChallengeSignature,
    attemptReference,
    returnUrl,
    signAuthorizations,
    additionalPaymentInfo,
  }: PrepareBidArgs) => {
    const settlementInfo = {
      currency: supportedCurrency,
      exchangeRateId: idFromObject(exchangeRate.id),
      paymentMethod: tokenPaymentMethod,
      ...(nonStackableMarketCredits &&
        conversionCreditIds && { conversionCreditIds }),
      savePaymentMethod,
      walletChallengeSignature,
      creditCardType: cardType,
      ...(returnUrl && { returnUrl }),
      ...(additionalPaymentInfo && { additionalPaymentInfo }),
      platform,
    };

    if (signAuthorizations) {
      await migrateEth();
    }

    const { data } = await prepareBidMutation({
      variables: {
        input: {
          auctionId,
          settlementInfo,
          amount,
          attemptReference,
        },
      },
    });

    const authorizations = data?.prepareBid?.authorizations || [];

    const signedAuthorizations =
      (signAuthorizations &&
        (await getAuthorizationApprovals(authorizations))) ||
      undefined;

    return {
      settlementInfo,
      authorizations,
      approvals: signedAuthorizations?.authorizationApprovals,
    };
  };

  return {
    prepareBid,
    loading,
  };
};
